/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { UserPermissions } from '@centecomm/portal-service';
import { useConversations, useCurrentUser } from '@centecomm/portal-service-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { conversationsSelectors } from 'State';
import { addSuccessMessage } from 'State/GlobalMessages/actionCreators';
import { ConfirmClosingConversation, ConfirmLeavingConversation } from '../ConfirmationPanel';
import { ConversationUsersPanel, RemoveUsersPanel } from '../ConversationUsersPanel';
import styles from './conversation-admin-panel.module.css';

interface ConversationAdminPanelProps {
    conversationId: number;
    closePanel: () => void;
}

type PanelViewStates = 0 | 1 | 2;
type ConfirmationViewStates = 0 | 1 | 2;

function ConversationAdminPanel({
    conversationId,
    closePanel,
}: ConversationAdminPanelProps): React.ReactElement<ConversationAdminPanelProps> | null {
    const { t } = useTranslation(['conversationsFeature']);
    const dispatch = useDispatch();

    const { endConversation, leaveConversation } = useConversations();

    const { hasPermission } = useCurrentUser();

    const conversationIsActive: boolean = useSelector(conversationsSelectors.conversationIsActive(conversationId));

    const [currentViewState, setCurrentViewState] = React.useState<PanelViewStates>(0);
    const [confirmationPanelViewState, setConfirmationPanelViewState] = React.useState<ConfirmationViewStates>(0);

    const close = () => {
        endConversation(conversationId).then(() => {
            dispatch(
                addSuccessMessage(t('conversationsFeature:adminPanel.confirmation.closingConversation.successMessage')),
            );
        });

        closePanel();
    };

    const leave = () => {
        leaveConversation(conversationId).then(() => {
            dispatch(
                addSuccessMessage(t('conversationsFeature:adminPanel.confirmation.leavingConversation.successMessage')),
            );
        });

        closePanel();
    };

    const resetConfirmationView = () => {
        setConfirmationPanelViewState(0);
    };

    const togglePanel = (confirmationViewState: ConfirmationViewStates) => {
        if (confirmationPanelViewState !== confirmationViewState) {
            setConfirmationPanelViewState(confirmationViewState);
        }
    };

    const viewStates = {
        1: <ConversationUsersPanel conversationId={conversationId} />,
        2: <RemoveUsersPanel conversationId={conversationId} />,
    };

    const confirmationViewStates = {
        1: <ConfirmClosingConversation confirm={close} cancel={resetConfirmationView} />,
        2: <ConfirmLeavingConversation confirm={leave} cancel={resetConfirmationView} />,
    };

    if (!conversationIsActive) return null;

    return (
        <>
            {currentViewState > 0 ? (
                viewStates[currentViewState]
            ) : (
                <>
                    {confirmationPanelViewState > 0 ? (
                        confirmationViewStates[confirmationPanelViewState]
                    ) : (
                        <div className={styles.ConversationAdminPanel}>
                            {hasPermission(UserPermissions.EndConversation) && (
                                <p
                                    onClick={() => {
                                        togglePanel(1);
                                    }}
                                >
                                    {t('conversationsFeature:adminPanel.actions.closeConversation')}
                                </p>
                            )}
                            <p
                                onClick={() => {
                                    togglePanel(2);
                                }}
                            >
                                {t('conversationsFeature:adminPanel.actions.leaveConversation')}
                            </p>
                            <p
                                onClick={() => {
                                    setCurrentViewState(1);
                                }}
                            >
                                {t('conversationsFeature:adminPanel.actions.addUsers')}
                            </p>
                            {hasPermission(UserPermissions.RemoveUserFromConversation) && (
                                <p
                                    onClick={() => {
                                        setCurrentViewState(2);
                                    }}
                                >
                                    {t('conversationsFeature:adminPanel.actions.removeUsers')}
                                </p>
                            )}
                        </div>
                    )}
                </>
            )}
        </>
    );
}

export default ConversationAdminPanel;
